// src/pages/LecturePage.jsx

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import languageMap, { normalizeLangCode, CANONICAL_NAMES } from "../components/languageMap";
import { useTranslation } from "react-i18next";
import db from '../db/lectureCacheDB';
import { LECTURE_CACHE_TTL_MS } from '../db/cacheConfig';

// --- 기능별 로직 분리 ---
import useVirtualizedScrollManager from './LecturePageScroll';
import createRowComponent from './LecturePageRow';
import LanguageSelector from './LecturePageLanguageSelector';
import LectureQuestionSlideUp from './LectureQuestionSlideUp';

// --- 아이콘 리소스 (ReactComponent: JS 번들에 인라인되어 별도 HTTP 요청 불필요) ---
import { ReactComponent as BackIconLight } from '../assets/icons/svg/lecture/light/back.svg';
import { ReactComponent as FocusIconLight } from '../assets/icons/svg/lecture/light/focus.svg';
import { ReactComponent as FontIconLight } from '../assets/icons/svg/lecture/light/font.svg';
import { ReactComponent as DarkModeLightIcon } from '../assets/icons/svg/lecture/light/darkmode.svg';
import { ReactComponent as LanguageLightIcon } from '../assets/icons/svg/lecture/light/global.svg';

import { ReactComponent as BackIconDark } from '../assets/icons/svg/lecture/dark/back.svg';
import { ReactComponent as FocusIconDark } from '../assets/icons/svg/lecture/dark/focus.svg';
import { ReactComponent as FontIconDark } from '../assets/icons/svg/lecture/dark/font.svg';
import { ReactComponent as DarkModeDarkIcon } from '../assets/icons/svg/lecture/dark/lightmode.svg';
import { ReactComponent as LanguageDarkIcon } from '../assets/icons/svg/lecture/dark/global.svg';
import { ReactComponent as QuestionLightIcon } from '../assets/icons/svg/lecture/light/question.svg';
import { ReactComponent as QuestionDarkIcon } from '../assets/icons/svg/lecture/dark/question.svg';

// =============================================================================
// CONSTANTS & CONFIGURATION
// =============================================================================

const MIN_FONT_STEP = -1;
const MAX_FONT_STEP = 2;
const CACHE_DEBOUNCE_MS = 400;
const MESSAGE_BUFFER_DELAY = 150; // 메시지 버퍼링 지연 (버스트 합치기)

// IndexedDB 키 생성 함수
const getCacheKey = (filterKey) => filterKey;
const getTimestampKey = (filterKey) => filterKey;

// =============================================================================
// 🛠 UTILITY FUNCTIONS
// =============================================================================

function getInitialDarkMode() {
    const savedMode = localStorage.getItem('lecturePageDarkMode');
    return savedMode !== null
        ? savedMode === 'true'
        : window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function deriveAvailableLanguagesFromMap(translationMap) {
    const languageSet = new Map();
    for (const group of translationMap.values()) {
        const languages = group?.languages || {};
        for (const [code, meta] of Object.entries(languages)) {
            const normalizedCode = normalizeLangCode(code);
            if (!languageSet.has(normalizedCode)) {
                const baseName = CANONICAL_NAMES[normalizedCode] || languageMap[code] || languageMap[normalizedCode] || normalizedCode;
                languageSet.set(normalizedCode, {
                    code: normalizedCode,
                    name: baseName,
                    isSource: !!meta?.isSource,
                });
            }
        }
    }
    return Array.from(languageSet.values());
}

function createDebounce(fn, wait = CACHE_DEBOUNCE_MS) {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
    };
}

const LecturePage = () => {
    const { t } = useTranslation();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const questionApiUrl = process.env.REACT_APP_QUESTION_API_URL;
    const navigate = useNavigate();
    const location = useLocation();
    const filterKey = location.state?.filterKey;

    const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
    const [fontSize, setFontSize] = useState(() => {
        const saved = localStorage.getItem('lecturePageFontSize');
        return saved !== null ? parseInt(saved, 10) : 0;
    });

    const [translationMap, setTranslationMap] = useState(new Map());
    const [selectedLanguageCode, setSelectedLanguageCode] = useState(() =>
        normalizeLangCode(localStorage.getItem('lecturePageLanguage')) || ""
    );
    const [isLanguageInitialized, setIsLanguageInitialized] = useState(false);
    const [showLanguageSelector, setShowLanguageSelector] = useState(false);
    const [showFontSlider, setShowFontSlider] = useState(false);
    const [shouldRedirect] = useState(!filterKey);
    const eventSourceRef = useRef(null);
    const [showQuestionPanel, setShowQuestionPanel] = useState(false);
    const [showOriginal, setShowOriginal] = useState(
        () => localStorage.getItem('lecturePageShowOriginal') === 'true'
    );

    // 🔧 캐싱 관련 state
    const [isRestoring, setIsRestoring] = useState(true);
    const [isCacheReady, setIsCacheReady] = useState(false);

    // 🚀 메시지 버퍼링 관련 refs
    const messageBuffer = useRef(new Map());
    const flushTimeouts = useRef(new Map());

    // 레이아웃 버전 (window resize 등으로 높이 재측정 트리거용)
    const [layoutVersion, setLayoutVersion] = useState(0);

    // 메모이제이션
    const translations = useMemo(() => Array.from(translationMap.values()), [translationMap]);
    const availableLanguages = useMemo(
        () => deriveAvailableLanguagesFromMap(translationMap),
        [translationMap]
    );
    const sourceLanguage = useMemo(
        () => availableLanguages.find(l => l.isSource) ?? null,
        [availableLanguages]
    );

    // --- 리스트 관련 ref ---
    const listRef = useRef(null);
    const rowHeights = useRef({});
    const rowRefs = useRef({});
    const lastReceivedTimestampRef = useRef("");
    const fontThumbRef = useRef(null);
    const fontTrackRef = useRef(null);
    const resizeTimeoutRef = useRef(null);
    const fontDragCleanupRef = useRef(null);

    const getRowHeight = useCallback(index => rowHeights.current[index] || 100, []);

    // 스크롤 동작 관련 커스텀 훅
    const {
        isAtBottom,
        showNewMessageButton,
        scrollToBottom,
        handleListScroll,
        handleReconnectionStart,
        saveScrollPosition,
        restoreScrollPosition,
        setListOuterElement,
        listOuterRef,
        notifyRowHeightChange, // 높이 안정화 게이트 신호
    } = useVirtualizedScrollManager(translations, listRef, rowHeights);

    const setListRef = useCallback((instance) => {
        listRef.current = instance;
    }, []);

    const getLanguageName = useCallback((code) => {
        const norm = normalizeLangCode(code);
        return CANONICAL_NAMES[norm] || languageMap[code] || languageMap[norm] || code;
    }, []);

    // 배치 저장 함수 (디바운스)
    const enqueueSave = useMemo(() => {
        return createDebounce(async (mapSnapshot, timestamp) => {
            try {
                const saveData = {
                    filterKey: getCacheKey(filterKey),
                    savedAt: Date.now(),
                    ttl: LECTURE_CACHE_TTL_MS,
                    data: Array.from(mapSnapshot.entries()),
                };
                await db.transaction('rw', [db.translations, db.lastTimestamps], async () => {
                    await db.translations.put(saveData);
                    if (timestamp) {
                        await db.lastTimestamps.put({
                            filterKey: getTimestampKey(filterKey),
                            value: timestamp
                        });
                    }
                });
            } catch (error) {
                console.warn('[Cache] 저장 실패:', error);
            }
        }, CACHE_DEBOUNCE_MS);
    }, [filterKey]);

    // 메시지 배치 처리
    const flushMessages = useCallback((groupId) => {
        const messages = messageBuffer.current.get(groupId);
        if (!messages || messages.length === 0) return;

        // 실제 초 단위 timestamp (ZRANGEBYSCORE 커서 및 IndexedDB lastTimestamp용)
        const actualTimestamp = messages[0]?._ts;

        setTranslationMap(prev => {
            const newMap = new Map(prev);
            const existingGroup = newMap.get(groupId);
            const group = existingGroup
                ? { ...existingGroup, languages: { ...existingGroup.languages } }
                : { groupId, languages: {} };

            let hasChanges = false;

            messages.forEach(({ srcLangType: srcLang, tarLangType: tarLang, srcText, translatedText }) => {
                const normSrc = normalizeLangCode(srcLang);
                const normTar = normalizeLangCode(tarLang);
                if (srcText && normSrc && !group.languages[normSrc]) {
                    group.languages[normSrc] = {
                        text: srcText,
                        isSource: true,
                        languageIndex: -1
                    };
                    hasChanges = true;
                }
                if (translatedText && normTar && !group.languages[normTar]) {
                    group.languages[normTar] = {
                        text: translatedText,
                        isSource: false,
                        languageIndex: 0
                    };
                    hasChanges = true;
                }
            });

            if (hasChanges) {
                newMap.set(groupId, group);
                enqueueSave(newMap, actualTimestamp);
            }
            return hasChanges ? newMap : prev;
        });

        // 정리
        messageBuffer.current.delete(groupId);
        flushTimeouts.current.delete(groupId);
    }, [enqueueSave]);

    // 버퍼 정리
    const cleanupBuffers = useCallback(() => {
        flushTimeouts.current.forEach(clearTimeout);
        flushTimeouts.current.clear();
        messageBuffer.current.clear();
    }, []);

    // 캐시 복원
    useEffect(() => {
        if (!filterKey) return;
        let canceled = false;

        const restoreCachedData = async () => {
            try {
                // TTL 만료 체크는 App.js cleanupExpiredCaches()에서 앱 시작 시 일괄 처리됨
                const cached = await db.translations.get(getCacheKey(filterKey));
                if (canceled || !cached) {
                    setIsRestoring(false);
                    setIsCacheReady(true);
                    return;
                }

                // 캐시에 저장된 언어코드 키를 현재 정규화 기준으로 변환
                // (이전 버전에서 JA, KO 등 대문자로 저장된 경우 대비)
                const restoredMap = new Map();
                for (const [groupId, group] of new Map(cached.data)) {
                    const normalizedLanguages = {};
                    for (const [code, meta] of Object.entries(group.languages || {})) {
                        const normCode = normalizeLangCode(code);
                        if (!normalizedLanguages[normCode]) {
                            normalizedLanguages[normCode] = meta;
                        }
                    }
                    restoredMap.set(groupId, { ...group, languages: normalizedLanguages });
                }
                if (canceled) return;

                const timestampRecord = await db.lastTimestamps.get(getTimestampKey(filterKey));
                if (timestampRecord?.value && !canceled) {
                    lastReceivedTimestampRef.current = timestampRecord.value;
                }

                setTranslationMap(restoredMap);

                // 높이 캐시 완전 초기화
                rowHeights.current = {};

                // 리스트 리셋은 다음 프레임에서
                requestAnimationFrame(() => {
                    listRef.current?.resetAfterIndex(0);
                    notifyRowHeightChange();
                });

            } catch (error) {
                console.warn('[Cache] 복원 실패:', error);
            } finally {
                if (!canceled) {
                    setIsRestoring(false);
                    setIsCacheReady(true);
                }
            }
        };

        restoreCachedData();
        return () => { canceled = true; };
    }, [filterKey, notifyRowHeightChange]);

    // 로컬스토리지 동기화
    useEffect(() => localStorage.setItem('lecturePageDarkMode', isDarkMode), [isDarkMode]);
    useEffect(() => localStorage.setItem('lecturePageShowOriginal', showOriginal), [showOriginal]);
    useEffect(() => localStorage.setItem('lecturePageFontSize', fontSize), [fontSize]);
    useEffect(() => {
        if (selectedLanguageCode) {
            localStorage.setItem('lecturePageLanguage', selectedLanguageCode);
        }
    }, [selectedLanguageCode]);

    // 수업 종료 SSE
    useEffect(() => {
        if (!filterKey) return;

        let isTerminated = false;
        const sessionToken = sessionStorage.getItem('lectureSessionToken') || '';
        const statusSource = new EventSource(`${baseUrl}/api/lecture/status?filterKey=${encodeURIComponent(filterKey)}&sessionToken=${encodeURIComponent(sessionToken)}`);

        statusSource.addEventListener('lecture_terminated', (event) => {
            try {
                JSON.parse(event.data); // data 참고 필요하면 사용할 수 있음
                isTerminated = true;
                statusSource.close();
                navigate("/lecture/end", { replace: true });
            } catch (err) {
                console.error('종료 신호 파싱 오류:', err);
            }
        });

        statusSource.onerror = (err) => {
            if (isTerminated) return;
            console.error('종료 SSE 오류:', err);
        };

        return () => {
            if (!isTerminated) {
                statusSource.close();
            }
        };
    }, [filterKey, navigate, baseUrl]);

    // 리스트 outerRef 연결 재시도
    useEffect(() => {
        let attempts = 0;
        const maxAttempts = 10;
        let timeoutId = null;

        const tryAttach = () => {
            if (listOuterRef.current) {
                setListOuterElement(listOuterRef.current);
            } else if (attempts < maxAttempts) {
                attempts++;
                timeoutId = setTimeout(tryAttach, 200);
            }
        };
        tryAttach();
        return () => { if (timeoutId) clearTimeout(timeoutId); };
    }, [setListOuterElement, listOuterRef]);

    // SSE(EventSource)
    useEffect(() => {
        if (!filterKey || isRestoring || !isCacheReady) return;

        let retryCount = 0;
        const maxRetries = 200;
        const retryDelay = 3000;
        let destroyed = false;
        let retryTimerId = null;

        const connectEventSource = async () => {
            if (destroyed) return;

            if (eventSourceRef.current) {
                try {
                    eventSourceRef.current.close();
                    eventSourceRef.current.onmessage = null;
                    eventSourceRef.current.onerror = null;
                    eventSourceRef.current = null;
                } catch {}
            }

            cleanupBuffers();
            handleReconnectionStart();

            const sessionToken = sessionStorage.getItem('lectureSessionToken') || '';
            const queryParams = new URLSearchParams({ filterKey, sessionToken });

            // 마지막 타임스탬프 재확인
            let currentTimestamp = lastReceivedTimestampRef.current;
            try {
                const tsRec = await db.lastTimestamps.get(getTimestampKey(filterKey));
                if (tsRec?.value) {
                    currentTimestamp = tsRec.value;
                    lastReceivedTimestampRef.current = tsRec.value;
                }
            } catch (e) {
                console.warn('[SSE] 타임스탬프 재확인 실패:', e);
            }
            if (currentTimestamp) queryParams.append('lastReceivedTimestamp', currentTimestamp);

            if (destroyed) return;

            const eventSource = new EventSource(`${baseUrl}/api/translation/stream?${queryParams}`);
            eventSourceRef.current = eventSource;

            eventSource.onmessage = (event) => {
                try {
                    const parsed = JSON.parse(event.data);
                    const {
                        redisKey,
                        timestamp,          // 초 단위 timestamp (ZRANGEBYSCORE 커서용)
                        srcLangType: srcLang = "ko",
                        tarLangType: tarLang = "en",
                        srcText,
                        translatedText
                    } = parsed;
                    // 6-part (translation:{univ}:{authCode}:{provider}:{ts}:{seq}):
                    // 7-part (translation:{univ}:{authCode}:{provider}:{ts}:{seq}:{langIdx}):
                    //   provider 제거 → 엔진이 달라도 같은 ts:seq는 한 행으로 묶음
                    //   groupId = translation:{univ}:{authCode}:{ts}:{seq}
                    const parts = redisKey ? redisKey.split(':') : null;
                    const groupId = parts && parts.length >= 6
                        ? `${parts[0]}:${parts[1]}:${parts[2]}:${parts[4]}:${parts[5]}`
                        : (redisKey ?? timestamp);

                    if (timestamp) {
                        lastReceivedTimestampRef.current = timestamp;
                    }

                    if (!messageBuffer.current.has(groupId)) {
                        messageBuffer.current.set(groupId, []);
                    }
                    const currentBuffer = messageBuffer.current.get(groupId);
                    currentBuffer.push({ srcLangType: srcLang, tarLangType: tarLang, srcText, translatedText, _ts: timestamp });

                    if (flushTimeouts.current.has(groupId)) {
                        clearTimeout(flushTimeouts.current.get(groupId));
                    }
                    const timeoutId = setTimeout(() => {
                        flushMessages(groupId);
                    }, MESSAGE_BUFFER_DELAY);
                    flushTimeouts.current.set(groupId, timeoutId);

                    retryCount = 0;
                } catch (err) {
                    console.error("❌ Parse error:", err);
                    return;
                }
            };

            eventSource.onerror = () => {
                try { eventSource.close(); } catch {}
                cleanupBuffers();
                if (!destroyed && retryCount < maxRetries) {
                    retryCount++;
                    retryTimerId = setTimeout(connectEventSource, retryDelay + Math.random() * 2000);
                }
            };
        };

        connectEventSource();
        return () => {
            destroyed = true;
            if (retryTimerId !== null) {
                clearTimeout(retryTimerId);
                retryTimerId = null;
            }
            if (eventSourceRef.current) {
                try {
                    eventSourceRef.current.close();
                    eventSourceRef.current.onmessage = null;
                    eventSourceRef.current.onerror = null;
                    eventSourceRef.current = null;
                } catch {}
            }
            cleanupBuffers();
        };
    }, [filterKey, isRestoring, isCacheReady, handleReconnectionStart, baseUrl, flushMessages, cleanupBuffers]);

    useEffect(() => {
        const handleResize = () => {
            if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);

            resizeTimeoutRef.current = setTimeout(() => {
                // 레이아웃 버전 증가 → Row useEffect 재실행 (새 높이 측정용)
                setLayoutVersion((v) => v + 1);

                requestAnimationFrame(() => {
                    // 높이/레이아웃 리셋
                    listRef.current?.resetAfterIndex(0);
                    notifyRowHeightChange();

                    // 레이아웃 반영된 뒤에 맨 아래로 보내기
                    setTimeout(() => {
                        scrollToBottom(true);
                    }, 50);
                });
            }, 150);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
        };
    }, [notifyRowHeightChange, scrollToBottom, setLayoutVersion]);


    // 폰트 조절 공통 함수
    const applyFontStep = useCallback((nextStep) => {
        const clamped = Math.max(MIN_FONT_STEP, Math.min(MAX_FONT_STEP, nextStep));
        saveScrollPosition();
        setFontSize(prev => {
            if (prev === clamped) return prev;

            rowHeights.current = {};

            requestAnimationFrame(() => {
                listRef.current?.resetAfterIndex(0);
                notifyRowHeightChange();

                if (isAtBottom) {
                    setTimeout(() => scrollToBottom(true), 0);
                } else {
                    setTimeout(restoreScrollPosition, 100);
                }
            });

            return clamped;
        });
    }, [notifyRowHeightChange, isAtBottom, scrollToBottom, saveScrollPosition, restoreScrollPosition]);

    const adjustFontSize = useCallback((delta) => {
        applyFontStep(fontSize + delta);
    }, [applyFontStep, fontSize]);


    // 언어 초기화
    useEffect(() => {
        if (!isLanguageInitialized && availableLanguages.length > 0) {
            const saved = normalizeLangCode(localStorage.getItem('lecturePageLanguage'));
            const codes = availableLanguages.map(l => l.code);
            const isValid = saved && codes.includes(saved);

            const fallback = availableLanguages.find(l => !l.isSource)?.code || codes[0];
            const newLang = isValid ? saved : fallback;

            setSelectedLanguageCode(prev => (prev === newLang ? prev : newLang));
            if (!isValid) localStorage.setItem('lecturePageLanguage', newLang);
            setIsLanguageInitialized(true);
        }
    }, [availableLanguages, isLanguageInitialized]);

    // 폰트 슬라이더 토글
    const toggleFontSlider = () => setShowFontSlider(prev => !prev);

    const handleMove = useCallback((clientY) => {
        const track = fontTrackRef.current;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        const y = clientY - rect.top;
        const percentage = Math.max(0, Math.min(1, y / rect.height));
        const nextStep = Math.round(-1 + (percentage * 3));
        applyFontStep(nextStep);
    }, [applyFontStep]);

    const handleMouseDown = (e) => {
        e.preventDefault();
        const move = (moveEvent) => handleMove(moveEvent.clientY);
        const up = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            fontDragCleanupRef.current = null;
        };
        fontDragCleanupRef.current = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        handleMove(e.clientY);
    };

    const handleTouchStart = useCallback((e) => {
        if (e.cancelable) e.preventDefault();
        const move = (moveEvent) => handleMove(moveEvent.touches[0].clientY);
        const up = () => {
            window.removeEventListener('touchmove', move);
            window.removeEventListener('touchend', up);
            fontDragCleanupRef.current = null;
        };
        fontDragCleanupRef.current = () => {
            window.removeEventListener('touchmove', move);
            window.removeEventListener('touchend', up);
        };
        window.addEventListener('touchmove', move, { passive: false });
        window.addEventListener('touchend', up);
        handleMove(e.touches[0].clientY);
    }, [handleMove]);

    useEffect(() => {
        const el = fontThumbRef.current;
        if (!el) return;
        el.addEventListener('touchstart', handleTouchStart, { passive: false });
        return () => el.removeEventListener('touchstart', handleTouchStart);
    }, [handleTouchStart]);

    // 드래그 중 unmount 시 리스너 정리
    useEffect(() => {
        return () => { fontDragCleanupRef.current?.(); };
    }, []);

    // restoreScrollPosition은 translations에 의존해 자주 바뀌므로 ref로 유지
    const restoreScrollRef = useRef(restoreScrollPosition);
    useEffect(() => { restoreScrollRef.current = restoreScrollPosition; }, [restoreScrollPosition]);
    const pendingScrollRestoreRef = useRef(false);

    // 원문 함께 보기 토글 시 스크롤 위치 저장 + 행 높이 초기화
    const handleShowOriginalChange = useCallback((next) => {
        if (!isAtBottom) {
            saveScrollPosition();
            pendingScrollRestoreRef.current = true;
        }
        setShowOriginal(next);
    }, [isAtBottom, saveScrollPosition]);

    useEffect(() => {
        const shouldRestore = pendingScrollRestoreRef.current;
        pendingScrollRestoreRef.current = false;
        rowHeights.current = {};
        requestAnimationFrame(() => {
            listRef.current?.resetAfterIndex(0);
            notifyRowHeightChange();
            if (shouldRestore) {
                setTimeout(() => restoreScrollRef.current?.(), 500);
            }
        });
    }, [showOriginal, notifyRowHeightChange]);

    // 언어 선택 토글 / 변경
    const toggleLanguageSelector = () => {
        setShowQuestionPanel(false);
        setShowLanguageSelector(prev => !prev);
    };
    const handleLanguageSelect = useCallback((langCode) => {
        if (langCode !== selectedLanguageCode) {
            const wasAtBottom = isAtBottom;
            if (!wasAtBottom) {
                saveScrollPosition();
            }
            setSelectedLanguageCode(langCode);
            setIsLanguageInitialized(true);
            setShowLanguageSelector(false);

            // 언어 변경 시 높이 완전 초기화
            rowHeights.current = {};
            requestAnimationFrame(() => {
                listRef.current?.resetAfterIndex(0);
                notifyRowHeightChange();
                if (wasAtBottom) {
                    setTimeout(() => scrollToBottom(true), 500);
                } else {
                    setTimeout(restoreScrollPosition, 500);
                }
            });
        } else {
            setShowLanguageSelector(false);
        }
    }, [saveScrollPosition, restoreScrollPosition, selectedLanguageCode, notifyRowHeightChange, isAtBottom, scrollToBottom]);

    // 현재 언어에 맞는 메시지 필터링
    const visibleGroups = useMemo(
        () => translations.filter(group => group.languages[selectedLanguageCode]),
        [translations, selectedLanguageCode]
    );

    // 행 높이 업데이트
    const setRowHeight = useCallback((index, size) => {
        const prev = rowHeights.current[index];
        if (prev !== size) {
            rowHeights.current[index] = size;

            // 크기 변화가 클 때만 리스트 리셋
            if (!prev || Math.abs(prev - size) > 5) {
                listRef.current?.resetAfterIndex(index);
            }

            notifyRowHeightChange();
        }
    }, [notifyRowHeightChange]);

    // 행 렌더링 함수 생성 (layoutVersion 전달)
    const Row = useMemo(() => createRowComponent({
        translations: visibleGroups,
        selectedLanguageCode,
        isAtBottom,
        isDarkMode,
        fontSize,
        rowRefs,
        setRowHeight,
        layoutVersion,
        showOriginal,
        originalLanguageCode: sourceLanguage?.code ?? null,
    }), [visibleGroups, selectedLanguageCode, isAtBottom, isDarkMode, fontSize, setRowHeight, layoutVersion, showOriginal, sourceLanguage]);

    const scrollButtonClasses = `fixed flex justify-center items-center rounded-xl w-14 h-14 shadow-md left-1/2 -translate-x-1/2 z-20 ${
        isDarkMode ? 'bg-[#4D4D4D] border-[#666]' : 'bg-white border-[#CCC]'
    } border-[0.5px]`;

    // 유효하지 않은 접근 시 홈으로 리다이렉트
    if (shouldRedirect) return <Navigate to="/" replace />;

    if (isRestoring) {
        return (
            <div className={`${isDarkMode ? 'dark' : ''}`}>
                <div className="h-screen w-full flex items-center justify-center bg-white dark:bg-[#1a1f2e]">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    return (
        <div className={`${isDarkMode ? 'dark' : ''}`}>
            <div className="h-screen w-full flex flex-col bg-white text-black dark:bg-[#1a1f2e] dark:text-white overflow-hidden">
                {/* Header */}
                <header className="fixed top-0 left-0 right-0 z-20 flex items-center h-14 bg-white dark:bg-[#0D0D0D] backdrop-blur-md">
                    <button
                        onClick={() => navigate('/')}
                        className="w-8 h-8 flex justify-center items-center ml-4"
                    >
                        {isDarkMode
                            ? <BackIconDark className="w-5 h-5" />
                            : <BackIconLight className="w-5 h-5" />
                        }
                    </button>
                </header>

                {/* Main content area */}
                <main className="flex-1 w-full overflow-hidden bg-[#F7F7F7] dark:bg-[#0D0D0D] shadow-md pt-[calc(env(safe-area-inset-top,0px)+3.5rem)] pb-[calc(env(safe-area-inset-bottom,0px)+4rem)]">
                    <div className="w-full max-w-[640px] lg:max-w-[944px] mx-auto h-full px-2">
                        <AutoSizer>
                            {({ height, width }) => {
                                const safeHeight = Math.max(height, 100);
                                return (
                                    <div className="relative h-full w-full">
                                        <List
                                            ref={setListRef}
                                            outerRef={listOuterRef}
                                            height={safeHeight}
                                            width={width}
                                            itemCount={visibleGroups.length}
                                            itemSize={getRowHeight}
                                            overscanCount={8}
                                            onScroll={handleListScroll}
                                            className="custom-scrollbar"
                                            initialScrollOffset={0}
                                        >
                                            {Row}
                                        </List>
                                    </div>
                                );
                            }}
                        </AutoSizer>
                    </div>
                </main>

                {/* Scroll to bottom button */}
                {showNewMessageButton && (
                    <button
                        onClick={() => { scrollToBottom(true); }}
                        className={`${scrollButtonClasses} transform transition-transform duration-200 ease-in-out bottom-[calc(env(safe-area-inset-bottom,0px)+6.5rem)]`}
                        aria-label="Scroll to bottom"
                    >
                        {isDarkMode
                            ? <FocusIconDark className="w-8 h-8" />
                            : <FocusIconLight className="w-8 h-8" />
                        }
                    </button>
                )}

                {/* Language selector */}
                <LanguageSelector
                    show={showLanguageSelector}
                    onClose={() => setShowLanguageSelector(false)}
                    availableLanguages={availableLanguages}
                    selectedLanguageCode={selectedLanguageCode}
                    onSelect={handleLanguageSelect}
                    onAutoSelect={(langCode) => {
                        setSelectedLanguageCode(langCode);
                        setIsLanguageInitialized(true);
                        rowHeights.current = {};
                    }}
                    getLanguageName={getLanguageName}
                    isDarkMode={isDarkMode}
                    defaultShowOriginal={showOriginal}
                    onShowOriginalChange={handleShowOriginalChange}
                />

                {/* Question SlideUp Panel */}
                <LectureQuestionSlideUp
                    show={showQuestionPanel}
                    onClose={() => setShowQuestionPanel(false)}
                    filterKey={filterKey}
                    baseUrl={baseUrl}
                    questionApiUrl={questionApiUrl}
                    isDarkMode={isDarkMode}
                    viewingLanguage={selectedLanguageCode}
                />

                {/* Footer */}
                <footer className="fixed bottom-0 left-0 right-0 z-[30] flex flex-col items-center w-full bg-white dark:bg-[#333] border-t border-[#CCC] dark:border-[#666]">
                    <div className="w-full max-w-[640px] lg:max-w-[944px] h-16 flex justify-center gap-12 sm:justify-evenly sm:gap-0 items-center px-6 mx-auto">
                        <button
                            onClick={() => setIsDarkMode(!isDarkMode)}
                            className="w-12 h-12 flex items-center justify-center rounded transition-colors duration-200 active:bg-black/[0.06] dark:active:bg-white/[0.06]"
                        >
                            {isDarkMode
                                ? <DarkModeDarkIcon className="w-10 h-10" />
                                : <DarkModeLightIcon className="w-10 h-10" />
                            }
                        </button>

                        <button
                            onClick={toggleLanguageSelector}
                            disabled={availableLanguages.length <= 1}
                            className={`w-12 h-12 flex items-center justify-center rounded transition-colors duration-200 ${
                                availableLanguages.length <= 1
                                    ? 'opacity-50'
                                    : showLanguageSelector
                                        ? 'bg-black/[0.06] dark:bg-white/[0.06]'
                                        : 'active:bg-black/[0.06] dark:active:bg-white/[0.06]'
                            }`}
                        >
                            {isDarkMode
                                ? <LanguageDarkIcon className="w-10 h-10" />
                                : <LanguageLightIcon className="w-10 h-10" />
                            }
                        </button>

                        <div className="relative">
                            <button
                                onClick={toggleFontSlider}
                                className={`w-12 h-12 flex items-center justify-center rounded transition-colors duration-200 ${
                                    showFontSlider
                                        ? 'bg-black/[0.06] dark:bg-white/[0.06]'
                                        : 'hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'
                                }`}
                            >
                                {isDarkMode
                                    ? <FontIconDark className="w-10 h-10" />
                                    : <FontIconLight className="w-10 h-10" />
                                }
                            </button>
                            {showFontSlider && (
                                <>
                                    <div
                                        className="fixed inset-0 z-29"
                                        onClick={() => setShowFontSlider(false)}
                                    />
                                    <div className="absolute bottom-full mb-4 left-1/2 transform -translate-x-1/2 z-30">
                                        <div className="w-12 h-49 gap-2 rounded-md border-[0.5px] border-[#CCCCCC] p-4 bg-white dark:bg-[#333] dark:border-[#666] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.05)]">
                                            <div className="flex flex-col items-center gap-2">
                                                <span
                                                    className="text-[10px] font-medium leading-[140%] tracking-[-0.01em] text-gray-700 dark:text-gray-300"
                                                   
                                                >
                                                    {t('lecture.fontSize.small')}
                                                </span>

                                                <div
                                                    ref={fontTrackRef}
                                                    className="relative h-28 w-1 bg-gray-200 dark:bg-[#4D4D4D] rounded-full cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        const y = e.clientY - rect.top;
                                                        const percentage = Math.max(0, Math.min(1, y / rect.height));
                                                        const newStep = Math.round(-1 + (percentage * 3));
                                                        const clampedStep = Math.max(-1, Math.min(2, newStep));

                                                        if (clampedStep !== fontSize) {
                                                            const delta = clampedStep - fontSize;
                                                            adjustFontSize(delta);
                                                        }
                                                    }}
                                                >
                                                    {[0, 1, 2, 3].map((index) => (
                                                        <div
                                                            key={index}
                                                            className="absolute w-2.5 h-1 bg-gray-200 dark:bg-[#4D4D4D] transform -translate-x-1/2 z-10"
                                                            style={{
                                                                top: `${(index / 3) * 100}%`,
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)'
                                                            }}
                                                        />
                                                    ))}

                                                    <div
                                                        className="absolute left-[-6px] -translate-y-1/2 w-4 h-4 bg-white border-[0.5px] border-[#E6E6E6] rounded-full shadow-[0px_2px_2px_0px_rgba(0,0,0,0.15)] transition-all duration-200 cursor-pointer z-20"
                                                        style={{
                                                            top: `${((fontSize + 1) / 3) * 100}%`
                                                        }}
                                                        ref={fontThumbRef}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onMouseDown={handleMouseDown}
                                                    />
                                                </div>

                                                <span
                                                    className="text-[16px] font-semibold leading-[140%] tracking-[-0.01em] text-gray-700 dark:text-gray-300"
                                                   
                                                >
                                                    {t('lecture.fontSize.large')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <button
                            onClick={() => { setShowLanguageSelector(false); setShowQuestionPanel(prev => !prev); }}
                            className={`w-12 h-12 flex items-center justify-center rounded transition-colors duration-200 ${
                                showQuestionPanel
                                    ? 'bg-black/[0.06] dark:bg-white/[0.06]'
                                    : 'active:bg-black/[0.06] dark:active:bg-white/[0.06]'
                            }`}
                        >
                            {isDarkMode
                                ? <QuestionDarkIcon className="w-10 h-10" />
                                : <QuestionLightIcon className="w-10 h-10" />
                            }
                        </button>
                    </div>

                    <div className="w-full h-safe-bottom shrink-0" />
                </footer>
            </div>
        </div>
    );
};

export default LecturePage;
